import { CometChatCalls } from '@cometchat/calls-sdk-javascript';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { COMETCHAT_CONSTANTS } from './AppConstants';
import './History.css';
import { Avatar, CallDetails, Icon } from './components';
import type { CallRecord } from './types/history';

const PAGE_SIZE = 10;
function getBaseUrl(): string {
  const r = COMETCHAT_CONSTANTS.REGION.toLowerCase();
  return `https://${COMETCHAT_CONSTANTS.APP_ID}.call-${r}.${COMETCHAT_CONSTANTS.IS_STAGING ? 'cometchat-staging.com' : 'cometchat.io'}/v3`;
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function History({ onLogout }: { onLogout: () => void }) {
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;
  const setCurrentPage = (updater: number | ((prev: number) => number)) => {
    const next = typeof updater === 'function' ? updater(currentPage) : updater;
    setSearchParams({ page: String(next) });
  };
  const [totalPages, setTotalPages] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isPaginating, setIsPaginating] = useState(false);
  const [paginationDir, setPaginationDir] = useState<'prev' | 'next' | null>(
    null,
  );
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedMid, setSelectedMid] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedCall = calls.find(c => c.mid === selectedMid) ?? null;

  // Refs for keyboard handler to avoid stale closures
  const callsRef = useRef(calls);
  callsRef.current = calls;
  const selectedMidRef = useRef(selectedMid);
  selectedMidRef.current = selectedMid;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const totalPagesRef = useRef(totalPages);
  totalPagesRef.current = totalPages;
  const isPaginatingRef = useRef(isPaginating);
  isPaginatingRef.current = isPaginating;

  // Fetch call history
  useEffect(() => {
    const fetchCalls = async () => {
      if (!isInitialLoad) {
        setIsPaginating(true);
      }
      try {
        const authToken = CometChatCalls.getUserAuthToken() || '';
        const res = await fetch(
          `${getBaseUrl()}/calls?page=${currentPage}&perPage=${PAGE_SIZE}`,
          { headers: { authtoken: authToken } },
        );
        const data = await res.json();
        const records: CallRecord[] = Array.isArray(data)
          ? data
          : (data.data ?? []);
        setCalls(records);
        if (data.meta?.pagination?.total != null) {
          setTotalPages(
            Math.max(1, Math.ceil(data.meta.pagination.total / PAGE_SIZE)),
          );
        } else {
          setTotalPages(
            records.length < PAGE_SIZE ? currentPage : currentPage + 1,
          );
        }
      } catch (err) {
        console.error('Failed to fetch call history:', err);
        setCalls([]);
      } finally {
        setIsInitialLoad(false);
        setIsPaginating(false);
        setPaginationDir(null);
      }
    };
    fetchCalls();
  }, [currentPage]);

  // Clear selection when page changes
  useEffect(() => {
    setSelectedMid(null);
  }, [currentPage]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcuts: ↑↓ to navigate rows, ←→ to paginate
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const list = callsRef.current;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (list.length === 0) return;
        const currentIdx = list.findIndex(
          c => c.mid === selectedMidRef.current,
        );
        const nextIdx = currentIdx < list.length - 1 ? currentIdx + 1 : 0;
        setSelectedMid(list[nextIdx].mid);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (list.length === 0) return;
        const currentIdx = list.findIndex(
          c => c.mid === selectedMidRef.current,
        );
        const prevIdx = currentIdx > 0 ? currentIdx - 1 : list.length - 1;
        setSelectedMid(list[prevIdx].mid);
      } else if (e.key === 'ArrowRight') {
        if (
          currentPageRef.current < totalPagesRef.current &&
          !isPaginatingRef.current
        ) {
          e.preventDefault();
          setPaginationDir('next');
          setCurrentPage(p => p + 1);
        }
      } else if (e.key === 'ArrowLeft') {
        if (currentPageRef.current > 1 && !isPaginatingRef.current) {
          e.preventDefault();
          setPaginationDir('prev');
          setCurrentPage(p => p - 1);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="history-container">
      {/* Top bar */}
      <div className="history-topbar">
        <div className="history-topbar__left">
          <h1 className="history-topbar__title">Call History</h1>
        </div>
        <div className="history-user-profile" ref={dropdownRef}>
          <div
            className="history-avatar-trigger"
            onClick={() => setShowUserDropdown(!showUserDropdown)}>
            <Avatar
              size={40}
              name={CometChatCalls.getLoggedInUser()?.name || ''}
              imageUrl={CometChatCalls.getLoggedInUser()?.avatar}
            />
          </div>
          {showUserDropdown && (
            <div className="history-dropdown-menu">
              <div className="history-dropdown-header">
                <div className="history-dropdown-avatar">
                  <Avatar
                    size={32}
                    name={CometChatCalls.getLoggedInUser()?.name || ''}
                    imageUrl={CometChatCalls.getLoggedInUser()?.avatar}
                  />
                </div>
                <div className="history-dropdown-user-name">
                  {CometChatCalls.getLoggedInUser()?.name || 'Guest User'}
                </div>
              </div>
              <div
                className="history-dropdown-item"
                onClick={() => {
                  onLogout();
                  setShowUserDropdown(false);
                }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor">
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                </svg>
                <span>Logout</span>
              </div>
              <div className="history-dropdown-divider" />
              <div className="history-dropdown-version">V.5.0.1</div>
            </div>
          )}
        </div>
      </div>

      {/* Split layout */}
      <div
        className={`history-split${selectedCall ? ' history-split--detail-open' : ''}`}>
        {/* Left: call list */}
        <div className="history-list-panel">
          {isInitialLoad ? (
            <div className="history-empty">
              <div className="loading-spinner" />
              <span>Loading call history...</span>
            </div>
          ) : calls.length === 0 ? (
            <div className="history-empty">
              <div className="history-empty-icon">
                <Icon
                  name="video-fill"
                  size={36}
                  fill="var(--cometchat-calls-text-color-disabled)"
                />
              </div>
              <span>
                No calls recorded yet. Start a session and it will show up here.
              </span>
            </div>
          ) : (
            <>
              <div className="history-list-scroll">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Session ID</th>
                      <th>Duration</th>
                      <th>Participants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls.map(call => (
                      <tr
                        key={call.mid}
                        className={
                          selectedMid === call.mid
                            ? 'history-row--selected'
                            : ''
                        }
                        onClick={() =>
                          setSelectedMid(prev =>
                            prev === call.mid ? null : call.mid,
                          )
                        }>
                        <td>
                          <div className="history-date-primary">
                            {formatDate(call.startedAt)}
                          </div>
                          <div className="history-date-secondary">
                            {formatTime(call.startedAt)}
                          </div>
                        </td>
                        <td>
                          <div className="history-session-cell">
                            <span
                              className="history-session-id"
                              title={call.sessionId}>
                              {call.sessionId.split('.').pop()}
                            </span>
                            {call.recordings && call.recordings.length > 0 && (
                              <span
                                className="history-recording-indicator"
                                title="Has recording"
                              />
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="history-duration">
                            {call.totalDuration}
                          </span>
                        </td>
                        <td>
                          <div className="history-participants">
                            <div className="history-participant-avatars">
                              {call.participants.slice(0, 3).map((p, i) => (
                                <div
                                  key={`${p.uid}-${i}`}
                                  className="history-participant-avatar">
                                  <Avatar
                                    name={p.name}
                                    imageUrl={p.avatar}
                                    size={30}
                                  />
                                </div>
                              ))}
                            </div>
                            <span className="history-participant-count">
                              {call.totalParticipants}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="history-pagination">
                <button
                  className="history-pagination-btn"
                  disabled={currentPage <= 1 || isPaginating}
                  onClick={() => {
                    setPaginationDir('prev');
                    setCurrentPage(p => p - 1);
                  }}
                  aria-label="Previous page">
                  {isPaginating && paginationDir === 'prev' ? (
                    <span className="history-pagination-spinner" />
                  ) : (
                    <Icon name="arrow-up" size={18} fill="currentColor" />
                  )}
                </button>
                <span className="history-page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="history-pagination-btn history-pagination-btn--next"
                  disabled={currentPage >= totalPages || isPaginating}
                  onClick={() => {
                    setPaginationDir('next');
                    setCurrentPage(p => p + 1);
                  }}
                  aria-label="Next page">
                  {isPaginating && paginationDir === 'next' ? (
                    <span className="history-pagination-spinner" />
                  ) : (
                    <Icon name="arrow-up" size={18} fill="currentColor" />
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right: detail panel */}
        {selectedCall ? (
          <CallDetails
            call={selectedCall}
            onClose={() => setSelectedMid(null)}
          />
        ) : (
          <div className="detail-placeholder">
            <div className="detail-placeholder-icon">
              <Icon
                name="video-fill"
                size={28}
                fill="var(--cometchat-calls-text-color-disabled)"
              />
            </div>
            <span>Select a call to view details</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default History;
